import { useState } from "react";
import { canAfford, deckCounts, offersFor, runStages, trimCost } from "@reagent/meta";
import { useGameStore } from "../../app/gameStore";
import { useRunStore } from "../../app/runStore";
import { CARD_CATEGORY_COLOR, getCardView } from "../cards/cardViews";
import "./shop.css";
import { playCue } from "../../audio/AudioEngine";

/**
 * Between stages: the supply room.
 *
 * The three levers of a deck-builder, and they pull against each other on one
 * budget — draft a card, trim one out, or upgrade the rig permanently.
 * Trimming is the one players undervalue and the one that matters most: the
 * cards you *don't* draw decide a hand as much as the ones you do, and it is
 * the only way to be rid of the hazards neglect put in your deck.
 */
export function ShopScreen() {
  const run = useRunStore((s) => s.run);
  const draft = useRunStore((s) => s.draft);
  const trim = useRunStore((s) => s.trim);
  const upgrade = useRunStore((s) => s.upgrade);
  const continueToNextStage = useRunStore((s) => s.continueToNextStage);
  const loadRunStage = useGameStore((s) => s.loadRunStage);

  const [trimming, setTrimming] = useState(false);

  if (run.status !== "shopping") return null;

  const offers = offersFor(run);
  const nextStage = runStages(run)[run.stageIndex + 1];
  const lastResult = run.results[run.results.length - 1];

  const onwards = () => {
    continueToNextStage();
    loadRunStage();
  };

  return (
    <div className="shop">
      <div className="shop__panel">
        <header className="shop__head">
          <div>
            <span className="shop__eyebrow">supply room</span>
            <h1 className="shop__title">Between batches</h1>
            {lastResult && (
              <p className="shop__earned">
                <span className="shop__earned-stage">
                  {lastResult.stageId.replace(/^stage-\d+-/, "").replace(/-/g, " ")}
                </span>{" "}
                paid <strong>{lastResult.creditsEarned}</strong> credits
              </p>
            )}
          </div>
          <div className="shop__credits">
            <span className="shop__credits-value">{run.credits}</span>
            <span className="shop__credits-label">credits</span>
          </div>
        </header>

        <section className="shop__section">
          <h2>On offer</h2>
          <div className="shop__offers">
            {offers.map((offer) => {
              if (offer.kind === "card") {
                const view = getCardView(offer.cardId);
                if (!view) return null;
                const affordable = canAfford(run, offer.cost);
                return (
                  <button
                    key={`card-${offer.cardId}`}
                    className={`shop__offer${affordable ? "" : " is-broke"}`}
                    disabled={!affordable}
                    onClick={() => {
                      playCue("purchase");
                      draft(offer.cardId);
                    }}
                    style={{ borderLeftColor: CARD_CATEGORY_COLOR[view.category] }}
                  >
                    <span className="shop__offer-name">{view.name}</span>
                    <span className="shop__offer-blurb">{view.blurb}</span>
                    <span className="shop__offer-foot">
                      <span className="shop__offer-tag">{view.category} · {view.costKw} kW</span>
                      <span className="shop__offer-cost">{offer.cost}</span>
                    </span>
                  </button>
                );
              }

              if (offer.kind === "upgrade") {
                const affordable = canAfford(run, offer.upgrade.cost);
                return (
                  <button
                    key={`up-${offer.upgrade.kind}`}
                    className={`shop__offer is-upgrade${affordable ? "" : " is-broke"}`}
                    disabled={!affordable}
                    onClick={() => {
                      playCue("purchase");
                      upgrade(offer.upgrade.kind);
                    }}
                  >
                    <span className="shop__offer-name">{offer.upgrade.name}</span>
                    <span className="shop__offer-blurb">{offer.upgrade.blurb}</span>
                    <span className="shop__offer-foot">
                      <span className="shop__offer-tag">permanent</span>
                      <span className="shop__offer-cost">{offer.upgrade.cost}</span>
                    </span>
                  </button>
                );
              }

              return (
                <button
                  key="trim"
                  className={`shop__offer is-trim${trimming ? " is-active" : ""}`}
                  onClick={() => setTrimming((v) => !v)}
                >
                  <span className="shop__offer-name">Strike a card</span>
                  <span className="shop__offer-blurb">
                    Remove one card from your deck for good. The best cut is the one you make
                    early.
                  </span>
                  <span className="shop__offer-foot">
                    <span className="shop__offer-tag">{trimming ? "pick one below" : "deck"}</span>
                    <span className="shop__offer-cost">{trimCost(run)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="shop__section">
          <h2>
            Your deck <span className="shop__count">{run.deck.length} cards</span>
          </h2>
          <div className="shop__deck">
            {deckCounts(run.deck).map(({ cardId, count }) => {
              const view = getCardView(cardId);
              if (!view) return null;
              const boundCount = run.deck.filter((ci) => ci.cardId === cardId && ci.boundNodeId !== null).length;
              const canStrike = trimming && canAfford(run, trimCost(run)) && run.deck.length > 5;
              return (
                <button
                  key={cardId}
                  className={`shop__card${trimming ? " is-strikable" : ""}`}
                  disabled={!canStrike}
                  title={view.blurb}
                  onClick={() => {
                    playCue("purchase");
                    trim(cardId);
                    setTrimming(false);
                  }}
                  style={{ borderLeftColor: CARD_CATEGORY_COLOR[view.category] }}
                >
                  <span className="shop__card-name">{view.name}</span>
                  <span className="shop__card-counts">
                    {count > 1 && <span className="shop__card-count">×{count}</span>}
                    {boundCount > 0 && <span className="shop__card-bound">{boundCount} bound</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <footer className="shop__foot">
          <span className="shop__next">
            Next: <strong>{nextStage?.name ?? "—"}</strong>
            {nextStage && <> — {nextStage.blurb}</>}
          </span>
          <button className="shop__go" onClick={onwards}>
            start the next batch
          </button>
        </footer>
      </div>
    </div>
  );
}
